import { useRef, useEffect, type PropsWithChildren, useState } from 'react';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box } from '@mui/material';
import { color } from '@/colors';

export const LAYER_PANEL_DND_TYPE = 'layer-panel';

type DnDWrapperProps = PropsWithChildren<{
  dragKey: number;
  index: number;
}>;

export function DnDWrapper({ dragKey, index, children }: DnDWrapperProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [target, setTarget] = useState<'top' | 'bottom'>();

  useEffect(() => {
    if (!ref.current) return;

    const draggableCleanup = draggable({
      element: ref.current,
      getInitialData: () => ({
        type: LAYER_PANEL_DND_TYPE,
        dragKey,
        index,
      }),
    });

    const dropTargetCleanup = dropTargetForElements({
      element: ref.current,
      getData: () => ({ type: LAYER_PANEL_DND_TYPE, dragKey, index }),
      canDrop: ({ source }) => source.data.type === LAYER_PANEL_DND_TYPE && source.data.dragKey !== dragKey,
      onDragEnter: ({ source }) => {
        setTarget(Number(source.data.index) < index ? 'top' : 'bottom');
      },
      onDragLeave: () => setTarget(undefined),
      onDrop: () => setTarget(undefined),
    });

    // Return cleanup function
    return () => {
      draggableCleanup();
      dropTargetCleanup();
    };
  }, [dragKey, index]);

  return (
    <Box
      ref={ref}
      id={`comp-layer-draggable-wrapper-${dragKey}`}
      component="div"
      key={dragKey}
      sx={{ position: 'relative' }}
    >
      {target && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            [target === 'top' ? 'top' : 'bottom']: 0,
            height: 2,
            background: color.Yellow100,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}
      {children}
    </Box>
  );
}
